import { create } from 'zustand';
import type { AuditEntryDto } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';

/**
 * The audit trail.
 *
 * Read-only, like the API behind it: there is no mutation here to call, which
 * is the point of a trail. The action list is fetched rather than hard-coded
 * (ADR-015) — new actions appear in the filter the day they start being
 * recorded, without a release of this screen.
 */

export interface AuditFilters {
  search: string;
  actions: string[];
  actorUserId: string;
  subjectKind: string;
  subjectId: string;
  /** `YYYY-MM-DD`, on the day it happened. */
  from: string;
  to: string;
}

export const EMPTY_AUDIT_FILTERS: AuditFilters = {
  search: '',
  actions: [],
  actorUserId: '',
  subjectKind: '',
  subjectId: '',
  from: '',
  to: '',
};

interface AuditState {
  items: AuditEntryDto[];
  nextCursor: string | null;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  filters: AuditFilters;
  total: number | null;
  /** Every action ever recorded, for the filter. */
  actions: string[];

  load: (filters?: Partial<AuditFilters>) => Promise<void>;
  loadMore: () => Promise<void>;
  loadActions: () => Promise<void>;
}

const PAGE_SIZE = 25;

// Only the latest request may write: filters typed or toggled in quick
// succession must not let an older page land over the newer one.
let listRequest = 0;

export const useAuditStore = create<AuditState>((set, get) => ({
  items: [],
  nextCursor: null,
  loading: false,
  loadingMore: false,
  error: null,
  filters: EMPTY_AUDIT_FILTERS,
  total: null,
  actions: [],

  async load(filters) {
    const next = { ...get().filters, ...filters };
    const request = ++listRequest;
    set({ loading: true, loadingMore: false, error: null, filters: next });

    try {
      const page = await withSession(() => adminApi.audit.list(toQuery(next, PAGE_SIZE)));
      if (request !== listRequest) return;
      set({
        items: page.items,
        nextCursor: page.meta.nextCursor,
        total: page.meta.total ?? null,
        loading: false,
      });
    } catch (error) {
      if (request !== listRequest) return;
      set({ loading: false, error: errorMessage(error, 'Could not load the audit log.') });
    }
  },

  async loadMore() {
    const { nextCursor, loadingMore, filters, items } = get();
    if (!nextCursor || loadingMore) return;

    const request = listRequest;
    set({ loadingMore: true });

    try {
      const page = await withSession(() =>
        adminApi.audit.list({ ...toQuery(filters, PAGE_SIZE), cursor: nextCursor }),
      );
      if (request !== listRequest) return;
      set({
        items: [...items, ...page.items],
        nextCursor: page.meta.nextCursor,
        loadingMore: false,
      });
    } catch (error) {
      if (request !== listRequest) return;
      set({ loadingMore: false, error: errorMessage(error, 'Could not load more entries.') });
    }
  },

  async loadActions() {
    try {
      const result = await withSession(() => adminApi.audit.actions());
      set({ actions: result.actions });
    } catch {
      // A missing filter list is not worth an error banner over the entries
      // themselves — the search box still reaches every action by name.
      set({ actions: [] });
    }
  },
}));

function toQuery(filters: AuditFilters, limit: number) {
  return {
    limit,
    search: filters.search || undefined,
    action: filters.actions.length > 0 ? filters.actions : undefined,
    actorUserId: filters.actorUserId || undefined,
    subjectKind: filters.subjectKind || undefined,
    subjectId: filters.subjectId || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
  };
}
